'use client';

import { ConditionalWrapper } from '@packages/shared/src/components/ConditionalWrapper/ConditionalWrapper';
import type { TooltipProps } from '@packages/modules/src/Tooltip/TooltipProps';
import { Tooltip } from '@packages/modules/src/Tooltip/Tooltip';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { Link } from '@packages/modules/src/Link/Link';
import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackCampaign/useTrackCampaign';
import { useRef } from 'react';
import { cva } from 'class-variance-authority';
import type { FlagshipTeaserProps } from './types';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { ComponentTypes, type LinkTarget } from '../../../interfaces';
import { classes } from '../../utils/classes';
import { useGetTooltipContent } from '../../hooks/useGetTooltipContent';
import { ImageTeaser } from '../ImageTeaser/ImageTeaser';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { LinkByButton } from '../LinkByButton/LinkByButton';
import { parseBorderRadius } from '../../utils/borderRadius';

const TooltipWrapper =
  (tooltipContent: string | null | undefined, tooltipPositioning: TooltipProps['positioning']) =>
  (ch: React.ReactNode) => (
    <Tooltip
      variant="default"
      content={tooltipContent ?? undefined}
      positioning={tooltipPositioning}
    >
      {ch}
    </Tooltip>
  );

const LinkWrapper =
  (finalLink: string, trackClick: () => void, linkTarget?: LinkTarget, linkRel?: 'nofollow') =>
  (ch: React.ReactNode) => (
    <Link
      href={finalLink}
      onClick={() => trackClick()}
      openInNewTab={!!(linkTarget && linkTarget === '_blank')}
      rel={linkRel}
    >
      {ch}
    </Link>
  );

/** This wrapper is needed to corectly apply padding */
const DistanceWrapper = (className?: string) => (ch: React.ReactNode) => (
  <div className={className}>{ch}</div>
);

const flexContainerStyles = cva('_c_cms_fsteaser flex flex-col rounded-[inherit] md:flex-row', {
  variants: {
    hasCurve: {
      true: '_c_cms_fsteaser_curved',
    },
    alignment: {
      left: '',
      center: 'text-center',
    },
  },
});

/**
 * The FlagshipTeaser component
 *
 * @param {FlagshipTeaserType} data - The data object
 * @returns A React component that renders a FlagshipTeaser
 */
export const FlagshipTeaser = ({ data, className }: FlagshipTeaserProps) => {
  const {
    // general styles
    behavior_paragraph_alignment: alignment = 'left',
    behavior_paragraph_variant: layoutVariant = 'image_only',
    behavior_paragraph_outer_spacing: layoutOuterSpacing = '0',
    behavior_paragraph_lazy_load: isLazyLoading = true,
    behavior_paragraph_text_color: fontColor,
    behavior_paragraph_background_color: backgroundColor,
    behavior_paragraph_borderRadius: borderRadius,

    behavior_paragraph_deco_line: hasDecoline,

    // texts
    field_text_01: kicker,
    field_kicker_subline: kickerSubline,
    field_button_text: buttonText,

    // link
    field_link: link,
    behavior_paragraph_link_target: linkTarget,
    behavior_paragraph_link_rel: linkRelRaw,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkSearchFilter,
    field_tracking: linkTracking,

    // custom identifier e.G. dynamic yield
    behavior_paragraph_custom_id: customId,

    // tooltip
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,

    // promotion item
    promotion_item: tooltipDataPromotion,
    content_snippet_bundled: tooltipDataContentSnippet,
  } = data;
  const linkRel = linkRelRaw === '_none' ? undefined : linkRelRaw;

  const tooltipContent = useGetTooltipContent(tooltipDataPromotion, tooltipDataContentSnippet);
  const flagshipRef = useRef<HTMLDivElement>(null);

  const tmpLink: string = link
    ? buildLinkWithArguments(link, {
        field_link_anchor: linkAnchor,
        field_link_highlight: linkHighlight,
        field_link_searchfilter: linkSearchFilter,
        field_tracking: linkTracking,
      })
    : '';
  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);
  const { trackClick } = useTrackMarketing(data, flagshipRef, finalLink);

  const parsedBorderRadius = borderRadius ? parseBorderRadius(borderRadius) : undefined;
  const borderRadiusStyle = {
    borderTopLeftRadius: parsedBorderRadius?.borderTopLeftRadius,
    borderTopRightRadius: parsedBorderRadius?.borderTopRightRadius,
    borderBottomRightRadius: parsedBorderRadius?.borderBottomRightRadius,
    borderBottomLeftRadius: parsedBorderRadius?.borderBottomLeftRadius,
  };

  const imageOnly = layoutVariant === 'image_only';
  const hasCurve = layoutVariant === 'split_layout_curve';

  const { isMobile } = useDeviceType();

  return (
    <ConditionalWrapper condition={Boolean(className)} wrapper={DistanceWrapper(className)}>
      <div
        className="bg-color-grey-light relative"
        style={{
          background: backgroundColor,
          margin: layoutOuterSpacing,
          ...borderRadiusStyle,
        }}
        {...(customId && { 'data-dy': customId })}
      >
        <ConditionalWrapper
          condition={!!(tooltipContent && !!tooltipActivation)}
          wrapper={TooltipWrapper(tooltipContent, tooltipPositioning)}
        >
          <ConditionalWrapper
            /* only link the full teaser if link is given and no button text is present */
            condition={!buttonText && finalLink !== ''}
            wrapper={LinkWrapper(finalLink, trackClick, linkTarget, linkRel)}
          >
            <div ref={flagshipRef} className={flexContainerStyles({ hasCurve, alignment })}>
              <ImageTeaser
                data={data}
                isLazyLoading={isLazyLoading}
                className={classes('w-full', !imageOnly && 'md:max-w-1/2')}
                style={{
                  borderTopLeftRadius: parsedBorderRadius?.borderTopLeftRadius,
                  ...((imageOnly || !isMobile) && {
                    borderBottomLeftRadius: parsedBorderRadius?.borderBottomLeftRadius,
                  }),
                  ...((imageOnly || isMobile) && {
                    borderTopRightRadius: parsedBorderRadius?.borderTopRightRadius,
                  }),
                  ...(imageOnly && {
                    borderBottomRightRadius: parsedBorderRadius?.borderBottomRightRadius,
                  }),
                }}
              />
              {!imageOnly && (
                <div
                  className={classes(
                    'md:max-w-1/2',
                    'flex items-center p-4', // center the whole text/button block vertically in case the image is taller than the text block
                    hasCurve && 'md:pl-0', // if curved, remove padding left to create a more balanced appearance
                    hasCurve && alignment === 'center' && 'md:-ml-3', // if centered and curved, move it a tiny bit to the left to create a more balanced appearance
                  )}
                >
                  <div>
                    <div className="mb-1.5 text-[6rem] leading-[6rem]" style={{ color: fontColor }}>
                      {kicker}
                    </div>
                    <div className="mb-1.5 text-[1.875rem] leading-4" style={{ color: fontColor }}>
                      {kickerSubline}
                    </div>

                    {buttonText &&
                      finalLink && ( // only show the button if link and button text are given
                        <LinkByButton
                          className="mt-1"
                          data={{
                            ...data, // contains glycerin data, button data and linkRel
                            type: 'link_by_button',
                            field_link: finalLink,
                            field_link_target: linkTarget,
                            field_text: buttonText,
                          }}
                        />
                      )}
                  </div>
                </div>
              )}
            </div>
          </ConditionalWrapper>
        </ConditionalWrapper>

        {hasDecoline && (
          // the top decoline needs to be only 1 spacing high, but also clip nicely on the edges when the Flagship has a much higher border radius
          <span
            className="pointer-events-none absolute top-0 left-0 flex h-full w-full overflow-hidden"
            style={borderRadiusStyle}
          >
            <span className="bg-color-primary h-1 w-full" />
          </span>
        )}
      </div>
    </ConditionalWrapper>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({
  [ComponentTypes.FlagshipTeaser]: FlagshipTeaser,
});
